import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Release } from '../../types/release'
import { cn } from '../../utils'
import { usePlayer } from '../../context/PlayerContext'
import { useToast } from '../../context/ToastContext'
import { useDiscogsConnection } from '../../context/DiscogsContext'
import { useDiscogsMembership } from '../../hooks/useDiscogsLists'
import { useDiscogsWrite } from '../../hooks/useDiscogsWrite'
import axiosClient from '../../api/client'
import { ArtistLinks } from '../common/ArtistLinks'
import styles from './ReleaseCrateView.module.css'

interface ReleaseCrateViewProps {
  releases: Release[]
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function getCover(release: Release | undefined): string {
  if (!release) return ''
  return release.images?.[0]?.uri || release.images?.[0]?.uri150 || ''
}

const ANIM_MS = 260

export function ReleaseCrateView({ releases }: ReleaseCrateViewProps) {
  const navigate = useNavigate()
  const [idx, setIdx] = useState(0)
  const [dispIdx, setDispIdx] = useState(0)
  const [animClass, setAnimClass] = useState('')
  const [selectedTrack, setSelectedTrack] = useState(0)
  const lockRef = useRef(false)
  const selectedRowRef = useRef<HTMLDivElement>(null)

  const { hasVideos, playRelease, currentTrack, isPlaying } = usePlayer()
  const { showToast } = useToast()
  const { isAuthenticated } = useDiscogsConnection()
  const { isInWantlist } = useDiscogsMembership()
  const { toggleWantlist } = useDiscogsWrite()

  // Display uses dispIdx; audio prefetch uses idx
  const release = releases[dispIdx]
  const next1 = releases[dispIdx + 1]
  const next2 = releases[dispIdx + 2]
  const videos = release?.videos ?? []

  const [audioCache, setAudioCache] = useState<Record<number, Record<string, any>>>({})

  // Fetch audio tags for the release being navigated to (idx)
  useEffect(() => {
    const rel = releases[idx]
    if (!rel) return
    if (audioCache[rel.id]) return
    axiosClient.get(`/releases/${rel.id}/video-audio`)
      .then(res => {
        if (res.data?.success) {
          setAudioCache(prev => ({ ...prev, [rel.id]: res.data.by_video_id }))
        }
      })
      .catch(() => {})
  }, [idx])

  const audioByVideoId = audioCache[release?.id ?? -1] ?? {}

  const getVideoId = (url: string) => {
    const m = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
    return m ? m[1] : null
  }

  const playTrack = useCallback((videoIndex: number, rel: Release) => {
    if (playRelease(rel, videoIndex) > 0) {
      setSelectedTrack(videoIndex)
    } else {
      showToast('Could not play this track', 'warning')
    }
  }, [playRelease, showToast])

  const goNext = useCallback(() => {
    if (lockRef.current || idx >= releases.length - 1) return
    lockRef.current = true
    const next = idx + 1
    setIdx(next)
    setAnimClass('animExitLeft')
    setTimeout(() => {
      setDispIdx(next)
      setAnimClass('animEnterRight')
      setSelectedTrack(0)
      setTimeout(() => {
        setAnimClass('')
        lockRef.current = false
        const nextRel = releases[next]
        if (nextRel && hasVideos(nextRel) && (nextRel.videos?.length ?? 0) > 0) {
          playTrack(0, nextRel)
        }
      }, ANIM_MS + 50)
    }, ANIM_MS)
  }, [idx, releases, hasVideos, playTrack])

  const goPrev = useCallback(() => {
    if (lockRef.current || idx <= 0) return
    lockRef.current = true
    const prev = idx - 1
    setIdx(prev)
    setAnimClass('animExitRight')
    setTimeout(() => {
      setDispIdx(prev)
      setAnimClass('animEnterLeft')
      setSelectedTrack(0)
      setTimeout(() => {
        setAnimClass('')
        lockRef.current = false
        const prevRel = releases[prev]
        if (prevRel && hasVideos(prevRel) && (prevRel.videos?.length ?? 0) > 0) {
          playTrack(0, prevRel)
        }
      }, ANIM_MS + 50)
    }, ANIM_MS)
  }, [idx, releases, hasVideos, playTrack])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return
      if (e.key === 'ArrowRight' || e.key === 'd') { e.preventDefault(); goNext() }
      else if (e.key === 'ArrowLeft' || e.key === 'a') { e.preventDefault(); goPrev() }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedTrack(i => Math.min(i + 1, videos.length - 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedTrack(i => Math.max(i - 1, 0)) }
      else if (e.key === 'Enter' && release && videos.length > 0) { e.preventDefault(); playTrack(selectedTrack, release) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [goNext, goPrev, videos.length, selectedTrack, release, playTrack])

  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedTrack])

  const handleToggleWantlist = () => {
    if (release) toggleWantlist(release)
  }

  if (!release) return <div className={styles.empty}>No releases to display</div>

  const cover = getCover(release)
  const cover1 = getCover(next1)
  const cover2 = getCover(next2)
  const releaseStyles = release.styles ?? []
  const labelLine = Array.isArray(release.formats) && release.formats.length > 0
    ? release.formats.map((f: any) => f.name).join(', ')
    : null

  return (
    <div className={styles.crateView}>

      {/* ── Left pane: 3D scene + nav ── */}
      <div className={styles.leftPane}>
        <div className={styles.scene}>
          <div className={styles.sceneInner}>
            {/* Back cover */}
            {cover2 && (
              <div className={styles.cover3dBack}>
                <img src={cover2} alt="" className={styles.coverImg} draggable={false} />
              </div>
            )}
            {/* Mid cover */}
            {cover1 && (
              <div className={styles.cover3dMid}>
                <img src={cover1} alt="" className={styles.coverImg} draggable={false} />
              </div>
            )}
            {/* Front cover — animated */}
            <div className={cn(styles.cover3dFront, animClass && (styles as any)[animClass])}>
              {cover ? (
                <img
                  src={cover}
                  alt={release.title}
                  className={styles.coverImg}
                  draggable={false}
                  onClick={() => navigate(`/releases/${release.id}`)}
                  style={{ cursor: 'pointer' }}
                />
              ) : (
                <div className={styles.coverPlaceholder}><i className="bi bi-disc"></i></div>
              )}
            </div>
          </div>
        </div>

        {/* Nav */}
        <div className={styles.navRow}>
          <button className={styles.navBtn} onClick={goPrev} disabled={idx === 0}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <span className={styles.counter}>{dispIdx + 1} / {releases.length}</span>
          <button className={styles.navBtn} onClick={goNext} disabled={idx >= releases.length - 1}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        {/* Keyboard hint */}
        <div className={styles.keyHint}>
          {['←', 'A', '·', '→', 'D'].map((k, i) => (
            <span key={i} className={k === '·' ? styles.keyDot : styles.keyBadge}>{k}</span>
          ))}
        </div>
      </div>

      {/* ── Right pane ── */}
      <div className={styles.rightPane}>

        {/* Title block */}
        <div className={styles.titleBlock}>
          {labelLine && <div className={styles.labelLine}>{labelLine}</div>}
          <h2 className={styles.releaseTitle}>{release.title}</h2>
          <div className={styles.artistLine}>
            <ArtistLinks artists={release.artists} />
            {release.year && <span className={styles.releaseYear}>{release.year}</span>}
          </div>
          {releaseStyles.length > 0 && (
            <div className={styles.stylePills}>
              {releaseStyles.slice(0, 5).map(s => (
                <span key={s} className={styles.styleTag}>{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          {hasVideos(release) && (
            <button className={styles.playBtn} onClick={() => playTrack(selectedTrack, release)}>
              <i className="bi bi-play-fill"></i> Play
            </button>
          )}
          <button
            className={cn(styles.actionBtn, isInWantlist(release.id) && styles.inWantlist)}
            onClick={handleToggleWantlist}
            disabled={!isAuthenticated}
            aria-pressed={isInWantlist(release.id)}
          >
            <i className={`bi ${isInWantlist(release.id) ? 'bi-heart-fill' : 'bi-heart'}`}></i>
            {isInWantlist(release.id) ? 'Wanted' : 'Want'}
          </button>
          <button className={styles.actionBtn} onClick={() => navigate(`/releases/${release.id}`)}>
            <i className="bi bi-info-circle"></i> Details
          </button>
        </div>

        {/* Tracklist */}
        <div className={styles.trackList}>
          {videos.length > 0 ? (
            <>
              <div className={styles.trackHeader}>
                <div className={cn(styles.trackHeaderCell, styles.colTitle)}>Title</div>
                <div className={cn(styles.trackHeaderCell, styles.colTime)}>Time</div>
                <div className={cn(styles.trackHeaderCell, styles.colBpm)}>BPM</div>
                <div className={cn(styles.trackHeaderCell, styles.colKey)}>Key</div>
                <div className={cn(styles.trackHeaderCell, styles.colStyles)}>Styles</div>
              </div>
              <div className={styles.trackRows}>
                {videos.map((video, i) => {
                  const isThisPlaying = isPlaying && currentTrack?.releaseId === release.id && currentTrack?.title === video.title
                  const isSelected = selectedTrack === i
                  const ytId = getVideoId(video.src || video.uri || '')
                  const audio = ytId ? audioByVideoId[ytId] : null
                  const displayStyles: string[] = audio?.styles?.length ? audio.styles : releaseStyles.slice(0, 4)

                  return (
                    <div
                      key={i}
                      ref={isSelected ? selectedRowRef : null}
                      className={cn(styles.trackRow, isThisPlaying && styles.trackPlaying, isSelected && !isThisPlaying && styles.trackSelected)}
                      onClick={() => { setSelectedTrack(i); playTrack(i, release) }}
                    >
                      <div className={cn(styles.trackCell, styles.colTitle)}>
                        {isThisPlaying
                          ? <i className={cn('bi bi-pause-fill', styles.playIcon)}></i>
                          : isSelected ? <i className={cn('bi bi-play-fill', styles.playIcon, styles.playIconDim)}></i> : null
                        }
                        {video.title}
                      </div>
                      <div className={cn(styles.trackCell, styles.colTime)}>
                        {video.duration ? formatDuration(video.duration) : <span className={styles.trackMuted}>—</span>}
                      </div>
                      <div className={cn(styles.trackCell, styles.colBpm)}>
                        {audio?.bpm ?? <span className={styles.trackMuted}>—</span>}
                      </div>
                      <div className={cn(styles.trackCell, styles.colKey)}>
                        {audio?.key ?? <span className={styles.trackMuted}>—</span>}
                      </div>
                      <div className={cn(styles.trackCell, styles.colStyles)}>
                        {displayStyles.map(s => <span key={s} className={styles.stylePill}>{s}</span>)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className={styles.noTracks}>
              <i className="bi bi-disc"></i> No playable tracks
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
